import { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Check,
  Pause,
  Play,
  Plus,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Separator, Textarea } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useSalesbots } from '@/features/inbox/api';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime, timeAgo } from '@/lib/format';
import {
  ACTION_LABELS,
  ENTITY_LABELS,
  OPERATOR_LABELS,
  TRIGGER_HINTS,
  TRIGGER_LABELS,
  useDeleteWorkflow,
  useSaveWorkflow,
  useSetWorkflowStatus,
  useWorkflowCatalogue,
  useWorkflowRuns,
  useWorkflows,
  type Action,
  type Condition,
  type ConditionGroup,
  type Trigger,
  type WorkflowRule,
} from './api';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  ACTIVE: 'default',
  DRAFT: 'outline',
  PAUSED: 'secondary',
};

/**
 * Automation rules (spec §5.9).
 *
 * A rule reads as a sentence — WHEN this happens, IF these hold, THEN do this —
 * and the log beside it shows every time it fired, every time it did not, and
 * why. A rules engine whose decisions are invisible is one people stop
 * trusting the first time it surprises them.
 */
export function WorkflowsPage() {
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState<WorkflowRule | 'new' | null>(null);
  const [showRuns, setShowRuns] = useState<WorkflowRule | null>(null);

  const { data: rules, isLoading } = useWorkflows(status ? { status } : {});
  const setRuleStatus = useSetWorkflowStatus();
  const remove = useDeleteWorkflow();

  return (
    <div>
      <PageHeader
        title="Automation rules"
        description="When something happens, if the conditions hold, do this. Every run is logged — including the ones that did not match."
        actions={
          <div className="flex items-center gap-2">
            <Select value={status} onChange={(event) => setStatus(event.target.value)} className="w-36">
              <option value="">All rules</option>
              <option value="ACTIVE">Active</option>
              <option value="DRAFT">Draft</option>
              <option value="PAUSED">Paused</option>
            </Select>
            <Button onClick={() => setEditing('new')}>
              <Plus className="me-1.5 h-3.5 w-3.5" />
              New rule
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={5} columns={4} />
            </div>
          ) : (rules ?? []).length === 0 ? (
            <div className="py-16 text-center">
              <Zap className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
              <p className="font-medium">No rules yet</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                A rule is how &ldquo;when a deal is won, create the project&rdquo; happens without
                anybody remembering to do it.
              </p>
              <Button className="mt-5" onClick={() => setEditing('new')}>
                Write the first one
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rule</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Runs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rules ?? []).map((rule) => (
                  <TableRow key={rule.id} className="cursor-pointer" onClick={() => setEditing(rule)}>
                    <TableCell>
                      <p className="text-sm font-medium">{rule.name}</p>
                      {rule.description && (
                        <p className="line-clamp-1 text-xs text-muted-foreground">{rule.description}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ENTITY_LABELS[rule.entityType] ?? rule.entityType} —{' '}
                      {TRIGGER_LABELS[rule.triggerType]}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowRuns(rule);
                        }}
                        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground hover:underline"
                      >
                        <Activity className="h-3.5 w-3.5" />
                        {rule.runCount}
                        {rule.errorCount > 0 && (
                          <span className="text-destructive">· {rule.errorCount} failing</span>
                        )}
                      </button>
                      {rule.lastRunAt && (
                        <p className="text-xs text-muted-foreground">{timeAgo(rule.lastRunAt)}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[rule.status]}>{rule.status.toLowerCase()}</Badge>
                    </TableCell>
                    <TableCell className="text-end">
                      <div
                        className="flex items-center justify-end gap-1"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setRuleStatus.mutate({
                              id: rule.id,
                              status: rule.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
                            })
                          }
                        >
                          {rule.status === 'ACTIVE' ? (
                            <>
                              <Pause className="me-1 h-3.5 w-3.5" />
                              Pause
                            </>
                          ) : (
                            <>
                              <Play className="me-1 h-3.5 w-3.5" />
                              Activate
                            </>
                          )}
                        </Button>
                        <button
                          onClick={() => remove.mutate(rule.id)}
                          className="p-1 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RunLog />

      {editing && (
        <RuleDialog rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {showRuns && <RunsDialog rule={showRuns} onClose={() => setShowRuns(null)} />}
    </div>
  );
}

function RunLog() {
  const { data } = useWorkflowRuns({ limit: 15 });

  if (!data || data.data.length === 0) return null;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">Recent activity</CardTitle>
        <CardDescription>
          Every evaluation, matched or not. &ldquo;Did not match&rdquo; is the answer to most
          questions about why nothing happened.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.data.map((run) => (
          <div key={run.id} className="flex items-start gap-3 border-b py-2 last:border-0">
            {run.matched ? (
              run.succeeded ? (
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              ) : (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              )
            ) : (
              <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm">
                <span className="font-medium">{run.rule.name}</span>
                <span className="text-muted-foreground">
                  {' '}
                  on {ENTITY_LABELS[run.entityType] ?? run.entityType}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                {run.matched
                  ? (run.results ?? []).map((result) => result.detail).join(' · ') || 'Matched'
                  : 'Conditions did not hold'}
                {run.error && <span className="text-destructive"> — {run.error}</span>}
              </p>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(run.ranAt)}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RunsDialog({ rule, onClose }: { rule: WorkflowRule; onClose: () => void }) {
  const { data } = useWorkflowRuns({ ruleId: rule.id, limit: 50 });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{rule.name}</DialogTitle>
          <DialogDescription>
            {data?.meta.total ?? 0} evaluations, newest first. Each one carries what it saw and what
            it decided.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[28rem] space-y-3 overflow-y-auto">
          {(data?.data ?? []).map((run) => (
            <div key={run.id} className="rounded-md border p-3">
              <div className="mb-2 flex items-center gap-2">
                <Badge variant={run.matched ? (run.succeeded ? 'default' : 'outline') : 'secondary'}>
                  {run.matched ? (run.succeeded ? 'ran' : 'failed') : 'no match'}
                </Badge>
                <span className="text-xs text-muted-foreground">{formatDateTime(run.ranAt)}</span>
                {run.durationMs !== null && (
                  <span className="text-xs text-muted-foreground">{run.durationMs}ms</span>
                )}
              </div>

              {(run.input?.conditions ?? []).length > 0 && (
                <div className="mb-2 space-y-0.5">
                  {run.input!.conditions.map((condition, index) => (
                    <p key={index} className="flex items-center gap-1.5 text-xs">
                      {condition.passed ? (
                        <Check className="h-3 w-3 text-emerald-600" />
                      ) : (
                        <X className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className="font-medium">{condition.field}</span>
                      <span className="text-muted-foreground">
                        {OPERATOR_LABELS[condition.operator] ?? condition.operator}
                      </span>
                      <span>{String(condition.expected ?? '—')}</span>
                      <span className="text-muted-foreground">
                        (was {String(condition.actual ?? 'empty')})
                      </span>
                    </p>
                  ))}
                </div>
              )}

              {(run.results ?? []).map((result, index) => (
                <p key={index} className="flex items-center gap-1.5 text-xs">
                  {result.ok ? (
                    <Check className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-3 w-3 text-destructive" />
                  )}
                  <span className="font-medium">{ACTION_LABELS[result.type] ?? result.type}</span>
                  <span className="text-muted-foreground">{result.detail}</span>
                </p>
              ))}
            </div>
          ))}
          {(data?.data ?? []).length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">
              This rule has not been evaluated yet.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RuleDialog({ rule, onClose }: { rule: WorkflowRule | null; onClose: () => void }) {
  const { data: catalogue } = useWorkflowCatalogue();
  const save = useSaveWorkflow();

  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [entityType, setEntityType] = useState(rule?.entityType ?? 'deal');
  const [triggerType, setTriggerType] = useState<Trigger>(rule?.triggerType ?? 'record_created');
  const [conditions, setConditions] = useState<ConditionGroup>(
    rule?.conditions ?? { join: 'AND', conditions: [] },
  );
  const [actions, setActions] = useState<Action[]>(rule?.actions ?? []);
  const [status, setStatus] = useState(rule?.status ?? 'DRAFT');

  const writable = catalogue?.writableFields[entityType] ?? [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit rule' : 'New rule'}</DialogTitle>
          <DialogDescription>
            A rule reads as a sentence. Keep it to one thing — several small rules are easier to
            switch off than one that does five.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[26rem] space-y-5 overflow-y-auto pe-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="rule-name">Name</Label>
              <Input
                id="rule-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Escalate a stale high-value deal"
              />
            </div>
            <div>
              <Label htmlFor="rule-status">Status</Label>
              <Select
                id="rule-status"
                value={status}
                onChange={(event) => setStatus(event.target.value as typeof status)}
              >
                <option value="DRAFT">Draft — does nothing yet</option>
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="rule-description">What it is for</Label>
            <Input
              id="rule-description"
              value={description ?? ''}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="So a big deal does not quietly go cold"
            />
          </div>

          <Separator />

          <div>
            <p className="eyebrow mb-2 text-[11px] text-muted-foreground">When</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="rule-entity">On a</Label>
                <Select
                  id="rule-entity"
                  value={entityType}
                  onChange={(event) => {
                    setEntityType(event.target.value);
                    setActions([]);
                  }}
                >
                  {(catalogue?.entities ?? []).map((entity) => (
                    <option key={entity} value={entity}>
                      {ENTITY_LABELS[entity] ?? entity}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="rule-trigger">When</Label>
                <Select
                  id="rule-trigger"
                  value={triggerType}
                  onChange={(event) => setTriggerType(event.target.value as Trigger)}
                >
                  {(catalogue?.triggers ?? []).map((trigger) => (
                    <option key={trigger} value={trigger}>
                      {TRIGGER_LABELS[trigger]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{TRIGGER_HINTS[triggerType]}</p>
          </div>

          <Separator />

          <ConditionEditor
            group={conditions}
            operators={catalogue?.operators ?? []}
            onChange={setConditions}
          />

          <Separator />

          <ActionEditor
            actions={actions}
            available={catalogue?.actions ?? []}
            writableFields={writable}
            entityType={entityType}
            onChange={setActions}
          />

          {save.isError && <FormError message={(save.error as ApiRequestError).message} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || actions.length === 0 || save.isPending}
            onClick={() =>
              save.mutate(
                {
                  id: rule?.id,
                  name,
                  description: description || undefined,
                  triggerType,
                  entityType,
                  conditions: conditions.conditions.length > 0 ? conditions : null,
                  actions,
                  status,
                },
                { onSuccess: onClose },
              )
            }
          >
            Save rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConditionEditor({
  group,
  operators,
  onChange,
}: {
  group: ConditionGroup;
  operators: string[];
  onChange: (group: ConditionGroup) => void;
}) {
  const update = (index: number, patch: Partial<Condition>) =>
    onChange({
      ...group,
      conditions: group.conditions.map((condition, position) =>
        position === index ? { ...condition, ...patch } : condition,
      ),
    });

  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <p className="eyebrow text-[11px] text-muted-foreground">If</p>
        {group.conditions.length > 1 && (
          <Select
            value={group.join}
            onChange={(event) => onChange({ ...group, join: event.target.value as 'AND' | 'OR' })}
            className="h-7 w-28 text-xs"
          >
            <option value="AND">all of these</option>
            <option value="OR">any of these</option>
          </Select>
        )}
        <button
          onClick={() =>
            onChange({
              ...group,
              conditions: [...group.conditions, { field: '', operator: 'eq', value: '' }],
            })
          }
          className="ms-auto text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus className="me-0.5 inline h-3 w-3" />
          Add condition
        </button>
      </div>

      {group.conditions.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-3 text-xs text-muted-foreground">
          No conditions — the rule fires every time the trigger happens. That is a legitimate thing
          to want, but say it deliberately.
        </p>
      ) : (
        <div className="space-y-2">
          {group.conditions.map((condition, index) => {
            const needsValue = !['is_empty', 'is_not_empty', 'changed'].includes(condition.operator);
            return (
              <div key={index} className="flex items-center gap-1.5">
                <Input
                  value={condition.field}
                  onChange={(event) => update(index, { field: event.target.value })}
                  placeholder="field, e.g. value or company.name"
                  className="h-8 flex-1 text-xs"
                />
                <Select
                  value={condition.operator}
                  onChange={(event) => update(index, { operator: event.target.value })}
                  className="h-8 w-40 text-xs"
                >
                  {operators.map((operator) => (
                    <option key={operator} value={operator}>
                      {OPERATOR_LABELS[operator] ?? operator}
                    </option>
                  ))}
                </Select>
                {needsValue && (
                  <Input
                    value={String(condition.value ?? '')}
                    onChange={(event) => update(index, { value: event.target.value })}
                    placeholder="value"
                    className="h-8 w-36 text-xs"
                  />
                )}
                <button
                  onClick={() =>
                    onChange({
                      ...group,
                      conditions: group.conditions.filter((_item, position) => position !== index),
                    })
                  }
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ActionEditor({
  actions,
  available,
  writableFields,
  entityType,
  onChange,
  stages,
}: {
  actions: Action[];
  available: string[];
  writableFields: string[];
  entityType: string;
  onChange: (actions: Action[]) => void;
  /** The board's stages, so "move to a stage" is a picker rather than an id. */
  stages?: { id: string; name: string }[];
}) {
  const { data: users } = useUserOptions();
  const { data: salesbots } = useSalesbots(available.includes('start_salesbot'));

  const update = (index: number, patch: Partial<Action>) =>
    onChange(actions.map((action, position) => (position === index ? { ...action, ...patch } : action)));

  const setConfig = (index: number, key: string, value: unknown) =>
    update(index, { config: { ...actions[index].config, [key]: value } });

  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <p className="eyebrow text-[11px] text-muted-foreground">Then</p>
        <button
          onClick={() => onChange([...actions, { type: available[0] ?? 'notify', config: {} }])}
          className="ms-auto text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus className="me-0.5 inline h-3 w-3" />
          Add action
        </button>
      </div>

      {actions.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-3 text-xs text-muted-foreground">
          A rule with no actions would do nothing. Add at least one.
        </p>
      ) : (
        <div className="space-y-2">
          {actions.map((action, index) => (
            <div key={index} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-2">
                <Select
                  value={action.type}
                  onChange={(event) => update(index, { type: event.target.value, config: {} })}
                  className="h-8 flex-1 text-xs"
                >
                  {available.map((type) => (
                    <option key={type} value={type}>
                      {ACTION_LABELS[type] ?? type}
                    </option>
                  ))}
                </Select>
                <button
                  onClick={() => onChange(actions.filter((_item, position) => position !== index))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              {action.type === 'update_field' && (
                <div className="flex gap-2">
                  <Select
                    value={String(action.config.field ?? '')}
                    onChange={(event) => setConfig(index, 'field', event.target.value)}
                    className="h-8 flex-1 text-xs"
                  >
                    <option value="">Choose a field</option>
                    {writableFields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </Select>
                  <Input
                    value={String(action.config.value ?? '')}
                    onChange={(event) => setConfig(index, 'value', event.target.value)}
                    placeholder="new value"
                    className="h-8 flex-1 text-xs"
                  />
                </div>
              )}

              {action.type === 'assign_owner' && (
                <Select
                  value={String(action.config.userId ?? '')}
                  onChange={(event) => setConfig(index, 'userId', event.target.value)}
                  className="h-8 text-xs"
                >
                  <option value="">Choose a person</option>
                  {(users ?? []).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
              )}

              {(action.type === 'create_task' || action.type === 'create_ticket') && (
                <div className="space-y-2">
                  <Input
                    value={String(action.config.title ?? action.config.subject ?? '')}
                    onChange={(event) =>
                      setConfig(
                        index,
                        action.type === 'create_task' ? 'title' : 'subject',
                        event.target.value,
                      )
                    }
                    placeholder="Follow up on {{title}}"
                    className="h-8 text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Anything in double braces is filled from the record — {'{{title}}'},{' '}
                    {'{{company.name}}'}.
                  </p>
                </div>
              )}

              {action.type === 'notify' && (
                <div className="space-y-2">
                  <Input
                    value={String(action.config.title ?? '')}
                    onChange={(event) => setConfig(index, 'title', event.target.value)}
                    placeholder="{{title}} has not moved in a fortnight"
                    className="h-8 text-xs"
                  />
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={Boolean(action.config.notifyOwner)}
                      onChange={(event) => setConfig(index, 'notifyOwner', event.target.checked)}
                    />
                    Tell whoever owns the record
                  </label>
                </div>
              )}

              {action.type === 'send_email' && (
                <div className="space-y-2">
                  <Input
                    value={String(action.config.to ?? '')}
                    onChange={(event) => setConfig(index, 'to', event.target.value)}
                    placeholder="{{email}} or a fixed address"
                    className="h-8 text-xs"
                  />
                  <Input
                    value={String(action.config.subject ?? '')}
                    onChange={(event) => setConfig(index, 'subject', event.target.value)}
                    placeholder="Subject"
                    className="h-8 text-xs"
                  />
                  <Textarea
                    value={String(action.config.body ?? '')}
                    onChange={(event) => setConfig(index, 'body', event.target.value)}
                    rows={3}
                    placeholder="Body"
                    className="text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    An address on the suppression list is never mailed, whatever the rule says.
                  </p>
                </div>
              )}

              {action.type === 'add_tag' && (
                <Input
                  value={String(action.config.tag ?? '')}
                  onChange={(event) => setConfig(index, 'tag', event.target.value)}
                  placeholder="at-risk"
                  className="h-8 text-xs"
                />
              )}

              {action.type === 'move_stage' &&
                (stages?.length ? (
                  <Select
                    value={String(action.config.stageId ?? '')}
                    onChange={(event) => setConfig(index, 'stageId', event.target.value)}
                    className="h-8 text-xs"
                  >
                    <option value="">Choose a stage</option>
                    {stages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    value={String(action.config.stageId ?? '')}
                    onChange={(event) => setConfig(index, 'stageId', event.target.value)}
                    placeholder="Stage id from the pipeline settings"
                    className="h-8 text-xs"
                  />
                ))}

              {action.type === 'send_message' && (
                <div className="space-y-2">
                  <Textarea
                    value={String(action.config.text ?? '')}
                    onChange={(event) => setConfig(index, 'text', event.target.value)}
                    rows={3}
                    placeholder="Hi {{firstName}}, thanks for your interest — when suits you for a call?"
                    className="text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Sent in the chat the customer last wrote from. On WhatsApp only inside the 24-hour window.
                  </p>
                </div>
              )}

              {action.type === 'start_salesbot' && (
                <Select
                  value={String(action.config.salesbotId ?? '')}
                  onChange={(event) => setConfig(index, 'salesbotId', event.target.value)}
                  className="h-8 text-xs"
                >
                  <option value="">Choose a Salesbot</option>
                  {(salesbots ?? []).map((bot) => (
                    <option key={bot.id} value={bot.id}>
                      {bot.name}
                    </option>
                  ))}
                </Select>
              )}

              {action.type === 'webhook' && (
                <div className="space-y-2">
                  <Input
                    value={String(action.config.url ?? '')}
                    onChange={(event) => setConfig(index, 'url', event.target.value)}
                    placeholder="https://example.com/hooks/dovo"
                    className="h-8 text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    https only, and never an address inside the network.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {entityType && writableFields.length === 0 && (
        <p className="mt-2 text-xs text-amber-700">
          A rule may not write any field on a {ENTITY_LABELS[entityType] ?? entityType}. Other
          actions still work.
        </p>
      )}
    </div>
  );
}
